import React, { useState } from "react";
import { toArabicNumerals, formatRating } from "@/lib/arabicNumerals";
import { useIsMobile } from "@/hooks/use-mobile";

type RatingBreakdownProps = {
  distribution: Record<number, number>;
  averageRating: number;
  total: number;
  activeStars?: number;
  onSelectStars: (star: number) => void;
  onClear: () => void;
};

const STAR_WORD: Record<number, string> = {
  1: "نجمة واحدة",
  2: "نجمتين",
  3: "٣ نجوم",
  4: "٤ نجوم",
  5: "٥ نجوم",
};

const RATING_ROWS = [5, 4, 3, 2, 1];

const RatingBreakdown: React.FC<RatingBreakdownProps> = ({
  distribution,
  averageRating,
  total,
  activeStars,
  onSelectStars,
  onClear,
}) => {
  const isMobile = useIsMobile();
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);

  const overlayWidth = `${(Math.max(0, Math.min(averageRating, 5)) / 5) * 100}%`;

  return (
    <div
      style={{
        background: "var(--ys-paper)",
        border: "1px solid var(--ys-rule)",
        borderRadius: "var(--ys-r-md)",
        boxShadow: "var(--ys-shadow-1)",
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "210px 1fr",
        gap: isMobile ? 12 : 30,
        alignItems: "center",
        padding: isMobile ? "14px 14px" : "24px 26px",
        fontFamily: "var(--ys-font)",
        width: "100%",
        maxWidth: isMobile ? "100%" : 640,
      }}
    >
      {/* Score column — on mobile the numeral, stars and count sit on one horizontal row */}
      <div
        style={{
          textAlign: "center",
          borderInlineEnd: isMobile ? "none" : "1px solid var(--ys-rule)",
          paddingInlineEnd: isMobile ? 0 : 30,
          borderBottom: isMobile ? "1px solid var(--ys-rule)" : "none",
          paddingBottom: isMobile ? 12 : 0,
          display: isMobile ? "flex" : "block",
          alignItems: isMobile ? "center" : undefined,
          justifyContent: isMobile ? "center" : undefined,
          gap: isMobile ? 10 : undefined,
          flexWrap: isMobile ? "wrap" : undefined,
        }}
      >
        <div
          style={{
            fontFamily: "var(--ys-font)",
            fontWeight: 700,
            fontSize: isMobile ? 30 : 54,
            lineHeight: 1,
            letterSpacing: "-.02em",
            color: "var(--ys-ink)",
          }}
        >
          {formatRating(averageRating)}
          <span
            style={{
              fontSize: isMobile ? 13 : 18,
              fontWeight: 600,
              color: "var(--ys-ink-3)",
            }}
          >
            {" / ٥"}
          </span>
        </div>
        <div
          style={{
            position: "relative",
            display: "inline-block",
            letterSpacing: 2,
            marginTop: isMobile ? 0 : 8,
          }}
        >
          <span style={{ color: "var(--ys-rule-2)" }}>★★★★★</span>
          <span
            style={{
              position: "absolute",
              insetInlineStart: 0,
              top: 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
              color: "var(--ys-star)",
              width: overlayWidth,
            }}
          >
            ★★★★★
          </span>
        </div>
        <div
          style={{
            fontSize: 12.5,
            color: "var(--ys-ink-3)",
            marginTop: isMobile ? 0 : 8,
          }}
        >
          مبني على{" "}
          <span style={{ color: "var(--ys-ink-2)", fontWeight: 600 }}>
            {toArabicNumerals(total)}
          </span>{" "}
          تقييم
        </div>
      </div>

      {/* Bars + filter status */}
      <div>
        {RATING_ROWS.map((star) => {
          const count = distribution[star] ?? 0;
          const isActive = activeStars === star;
          const isHovered = hoveredStar === star;
          const fillWidth = total === 0 ? "0%" : `${(count / total) * 100}%`;
          const pct = total === 0 ? 0 : Math.round((count / total) * 100);

          let rowBackground = "transparent";
          if (isActive) rowBackground = "var(--ys-primary-soft)";
          else if (isHovered) rowBackground = "var(--ys-bg-2)";

          const labelColor = isActive ? "var(--ys-primary-hover)" : "var(--ys-ink-2)";
          const countColor = isActive ? "var(--ys-primary-hover)" : "var(--ys-ink-2)";
          const fillBackground = isActive ? "var(--ys-primary)" : "var(--ys-star)";

          return (
            <button
              key={star}
              type="button"
              onClick={() => onSelectStars(star)}
              onMouseEnter={() => setHoveredStar(star)}
              onMouseLeave={() => setHoveredStar((prev) => (prev === star ? null : prev))}
              style={{
                border: 0,
                background: rowBackground,
                cursor: "pointer",
                padding: isMobile ? "3px 6px" : "5px 8px",
                borderRadius: "var(--ys-r)",
                textAlign: "start",
                fontFamily: "var(--ys-font)",
                transition: "background .15s",
                display: "grid",
                gridTemplateColumns: isMobile ? "36px 1fr 62px" : "54px 1fr 86px",
                gap: isMobile ? 8 : 14,
                alignItems: "center",
                width: "100%",
              }}
            >
              {/* Label cell */}
              <span
                style={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: labelColor,
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                {toArabicNumerals(star)}
                <span style={{ color: "var(--ys-star)", fontSize: 12 }}>★</span>
              </span>

              {/* Track */}
              <span
                style={{
                  height: 9,
                  borderRadius: "var(--ys-r-pill)",
                  background: "var(--ys-bg-2)",
                  overflow: "hidden",
                  display: "block",
                }}
              >
                <span
                  style={{
                    display: "block",
                    height: "100%",
                    borderRadius: "var(--ys-r-pill)",
                    background: fillBackground,
                    width: fillWidth,
                  }}
                />
              </span>

              {/* Meta cell */}
              <span
                style={{
                  fontSize: 12.5,
                  display: "flex",
                  gap: 8,
                  alignItems: "baseline",
                }}
              >
                <span
                  style={{
                    color: countColor,
                    fontWeight: 600,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {toArabicNumerals(count)}
                </span>
                <span style={{ color: "var(--ys-ink-3)" }}>
                  {toArabicNumerals(pct)}٪
                </span>
              </span>
            </button>
          );
        })}

        {/* Filter status line */}
        <div
          style={{
            marginTop: isMobile ? 10 : 14,
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            fontSize: 13,
            color: "var(--ys-ink-2)",
          }}
        >
          {activeStars === undefined ? (
            <span style={{ color: "var(--ys-ink-3)" }}>
              تعرض الآن جميع التقييمات ({toArabicNumerals(total)})
            </span>
          ) : (
            <>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: "var(--ys-primary-soft)",
                  border: "1px solid var(--ys-primary-tint)",
                  color: "var(--ys-primary-hover)",
                  borderRadius: 999,
                  padding: "5px 12px",
                  fontWeight: 600,
                  fontSize: 12.5,
                }}
              >
                {STAR_WORD[activeStars]} ·{" "}
                {toArabicNumerals(distribution[activeStars] ?? 0)}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClear();
                  }}
                  style={{
                    border: 0,
                    background: "transparent",
                    color: "var(--ys-primary-hover)",
                    cursor: "pointer",
                    fontSize: 14,
                    padding: 0,
                    lineHeight: 1,
                  }}
                  aria-label="إلغاء الفلتر"
                >
                  ✕
                </button>
              </span>
              <span style={{ color: "var(--ys-ink-3)" }}>
                من إجمالي {toArabicNumerals(total)} تقييم
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RatingBreakdown;
